import {zodResolver} from '@hookform/resolvers/zod';
import {useIsFocused} from '@react-navigation/native';
import React from 'react';
import {useForm} from 'react-hook-form';
import {Image, StyleSheet, View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {z} from 'zod';

import {backgroundRegister} from '../../assets/images';
import {Alert} from '../../components/Alert';
import {LoadingButton} from '../../components/Button';
import {Input} from '../../components/Form/Input';
import {InputContainer} from '../../components/Form/InputContainer';
import {ValidatedField} from '../../components/Form/ValidatedField';
import {inputIconProps} from '../../components/Form/util';
import {Text} from '../../components/Typography';
import {
  Alerts,
  ErrorMessages,
  Strings,
  interpolate,
  texts,
} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {MailIcon} from '../../icons';
import {userApi} from '../../lib/api/apis';
import {useAdaptiveSafeArea} from '../../lib/hooks/useAdaptiveSafeArea';
import {useBreakpoints} from '../../lib/hooks/useBreakpoints';
import {setUser, userSelector} from '../../lib/redux/slices/userSlice';
import {updateUser} from '../../lib/utils/updateUser';
import type {HomeEmulatedDrawerNavigatorScreenProps} from '../../navigators/HomeDrawerNavigator';
import {centerText} from '../../styles';
import {LogoHeader, LogoTitle, TermsAndPrivacy} from '../components';
import {KeyboardAvoidingLayout} from '../components/composable';
import {authStyles} from '../login/styles';
import {screenColorScheme} from '../screenColorScheme';

interface Props
  extends HomeEmulatedDrawerNavigatorScreenProps<typeof Screens.EmailVerify> {}

export const EmailVerifyScreen = ({navigation}: Props) => {
  useAdaptiveSafeArea();

  const isFocused = useIsFocused();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);

  const {isSmall} = useBreakpoints();

  const defaultValues: EmailVerifyValues = {
    email: user?.email ?? '',
  };

  const schema = z.object({
    email: z
      .string()
      .trim()
      .nonempty(ErrorMessages.REQUIRED_EMAIL)
      .email(ErrorMessages.INVALID_EMAIL)
      .transform(val => val.trim())
      .superRefine(async (value, ctx) => {
        if (!value || !value.length || value === user?.email) return true;

        const {data} = await userApi
          .validateFields({
            userValidateFieldsDto: {email: value},
          })
          .catch(() => ({data: undefined}));

        if (!data) return true;

        if (data.email === 'ok') return true;

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            data.email === 'taken'
              ? ErrorMessages.EMAIL_ALREADY_USED
              : ErrorMessages.INVALID_EMAIL,
        });
      }),
  });

  type EmailVerifyValues = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    setError,
    formState: {isSubmitting, errors},
  } = useForm<EmailVerifyValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
  });

  const handleSendVerification = async () => {
    await userApi.requestEmailVerification({id: 'me'});

    Alert.alert(
      Alerts.REQUEST_EMAIL_VERIFICATION,
      interpolate(Alerts.REQUEST_EMAIL_VERIFICATION_SUCCESS, {
        email: user?.email,
      }),
    );

    navigation.goBack();
  };

  const onSubmit = async (values: EmailVerifyValues) => {
    if (values.email === user?.email) return handleSendVerification();

    if (values.email !== user?.email) {
      const newEmail = values.email.trim();
      await updateUser(dispatch, {email: newEmail});
    }

    handleSendVerification();
  };

  // Update user when the user navigates away
  React.useEffect(() => {
    if (isFocused) return;

    userApi.getUserById({id: 'me'}).then(r => dispatch(setUser(r.data)));
  }, [isFocused]);

  return (
    <KeyboardAvoidingLayout style={styles.container}>
      <Image style={styles.image} source={backgroundRegister} />

      <View style={[authStyles.innerContainer, styles.innerContainer, {}]}>
        <View style={styles.flex}>
          <LogoHeader style={styles.header}>
            <LogoTitle>{texts.screens.verifyEmail.title}</LogoTitle>
          </LogoHeader>

          <Text variant="s" style={[centerText, styles.description]}>
            {texts.screens.verifyEmail.subtitle}
          </Text>

          <View style={styles.fields}>
            <ValidatedField {...{control}} name="email">
              {props => (
                <InputContainer invalid={!!errors.email}>
                  <MailIcon {...inputIconProps} />
                  <Input
                    keyboardType="email-address"
                    placeholder={Strings.EMAIL_ADDRESS}
                    {...props}
                  />
                </InputContainer>
              )}
            </ValidatedField>
          </View>

          <LoadingButton
            loading={isSubmitting}
            disabled={isSubmitting}
            style={{marginTop: isSmall ? 20 : 38}}
            onPress={handleSubmit(values =>
              onSubmit(values).catch(e => {
                setError('root', {message: e.message});
              }),
            )}>
            {Strings.CONFIRM_EMAIL}
          </LoadingButton>
        </View>

        <Text variant="s" style={[centerText, styles.footer]}>
          <TermsAndPrivacy />
        </Text>
      </View>
    </KeyboardAvoidingLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    flex: 1,
  },
  image: {zIndex: -10, position: 'absolute'},
  innerContainer: {
    backgroundColor: screenColorScheme.backgroundColor,
    borderTopRightRadius: 45,
    borderTopLeftRadius: 45,
    height: '75%',
  },
  flex: {
    flex: 1,
  },
  fields: {
    marginTop: 10,
  },
  header: {marginTop: 40},
  description: {
    marginTop: 10,
    marginBottom: 25,
  },
  footer: {
    marginHorizontal: 10,
  },
});
